import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { GO_SERVICE_QUERY_KEYS } from "../queryKeys";
import { getServiceFeedback } from "../services/goHireService";
import { ServiceFeedback } from "../../types/service.type";
import { ServiceFeedbackResponse } from "../types/serviceFeedback.types";

const FEEDBACK_PAGE_LIMIT = 10;

interface UseServiceFeedbackParams {
  userId?: number;
  serviceId?: number;
  limit?: number;
  rating?: number;
  requestId?: number;
}

export const getQueryKey = (params: UseServiceFeedbackParams) => {
  const { userId, serviceId, limit, rating, requestId } = params;
  if (userId) {
    return [...GO_SERVICE_QUERY_KEYS.serviceFeedback(userId), { serviceId, limit, rating }];
  }
  if (serviceId) {
    return [...GO_SERVICE_QUERY_KEYS.service(serviceId), { limit, rating }];
  }
  if (requestId) {
    return [...GO_SERVICE_QUERY_KEYS.serviceRequest(requestId), { limit, rating, serviceId, requestId }];
  }
  throw new Error("Either userId or serviceId or requestId must be provided");
};

export const useServiceFeedbackInfinite = ({
  userId,
  serviceId,
  limit = FEEDBACK_PAGE_LIMIT,
  rating,
  requestId,
}: UseServiceFeedbackParams) => {
  const isEnabled = Boolean(userId || serviceId || requestId);
  const queryKey = isEnabled ? getQueryKey({ userId, serviceId, limit, rating, requestId }) : [];

  return useInfiniteQuery<
    ServiceFeedbackResponse,
    Error,
    { items: ServiceFeedback[]; meta: ServiceFeedbackResponse["meta"] }
  >({
    queryKey,
    queryFn: ({ pageParam }) => {
      const currentPage = (pageParam as number | undefined) ?? 0;
      if (!userId && !serviceId && !requestId) {
        throw new Error("Either userId or serviceId or requestId must be provided");
      }
      return getServiceFeedback(userId, limit, currentPage * limit, serviceId, rating, requestId);
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * limit;
      return fetchedCount < lastPage.meta.totalItems ? allPages.length : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((page) => page.items),
      meta: data.pages[0].meta,
    }),
    initialPageParam: 0,
    enabled: isEnabled,
  });
};

interface UseServiceFeedbackPaginatedParams extends UseServiceFeedbackParams {
  page?: number;
}

export const useServiceFeedbackPaginated = ({
  userId,
  serviceId,
  limit = FEEDBACK_PAGE_LIMIT,
  page = 0,
  requestId,
  rating,
  enabled,
}: UseServiceFeedbackPaginatedParams & { enabled?: boolean }) => {
  const isEnabled =
    enabled !== undefined
      ? enabled && Boolean(userId || serviceId || requestId)
      : Boolean(userId || serviceId || requestId);
  const queryKey = isEnabled ? getQueryKey({ userId, serviceId, limit, rating, requestId }) : [];

  return useQuery<ServiceFeedbackResponse, Error, ServiceFeedback[]>({
    queryKey,
    queryFn: () => {
      if (!userId && !serviceId && !requestId) {
        throw new Error("Either userId or serviceId or requestId must be provided");
      }
      return getServiceFeedback(userId, limit, page * limit, serviceId, rating, requestId);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => data.items,
    enabled: isEnabled,
  });
};
